import {useEffect, type FC} from "react"
import {Box, Skeleton, Stack} from "@mui/material"
import {DiscoverSidePanelItem} from "../../molecules/DiscoverSidePanelItem"
import ImageComponent from "../../atoms/imageContainer/ImageContainer"
import {thoughtPiecesEndpoints} from "../../../services/api/thought-pieces"
import {useQuery} from "@tanstack/react-query"
import {ThoughtPiecesModel} from "../../../types/ThoughtPiecesModel"
import {ErrorMessage} from "../../molecules/ErrorMessage"
import {useConfigProvider} from "../../../config"
import {useClientSessionStore} from "../../../stores/clientSessionStore"
import {Link} from "react-router-dom"
import useSelectedThoughtPiecesStore from "../../../stores/fullPageThoughtPiecesStore"
import TextSeparator from "../../atoms/separator/TextSeparator"
import moment from "moment"
import {useSessionStorage} from "usehooks-ts"

const DiscoverSidePanel: FC = () => {
  const {setId} = useSelectedThoughtPiecesStore()
  const {getThoughtPiecesAnalysisList} = thoughtPiecesEndpoints()
  const {cmsImageUrl} = useConfigProvider()
  const sessionClient = useClientSessionStore((state) => state.crmAccount)
  const {
    data: thoughtPieces,
    isLoading,
    isFetching,
    isRefetching,
    isError,
    isLoadingError,
    isRefetchError,
    refetch,
  } = useQuery<ThoughtPiecesModel[]>({
    queryKey: ["getThoughtPiecesAnalysisList"],
    queryFn: async () => await getThoughtPiecesAnalysisList(),
    enabled: !!sessionClient,
  })
  var borderCondition =
    !sessionClient ||
    isLoading ||
    isFetching ||
    isRefetching ||
    (!thoughtPieces && !isError && !isLoadingError && !isRefetchError)
      ? "none"
      : !thoughtPieces ||
        isError ||
        isLoadingError ||
        isRefetchError ||
        !Array.isArray(thoughtPieces) ||
        thoughtPieces.length === 0
      ? "none"
      : thoughtPieces.length > 0
      ? "solid 1px #eeeeee"
      : "none"

  const [language] = useSessionStorage("language", "en")
  useEffect(() => {
    if (language) {
      moment.locale(language)
    }
  }, [language])

  return (
    <Box
      position={"relative"}
      borderRadius={"8px"}
      height={"100%"}
      display={"flex"}
      padding={"8px"}
      justifyContent={"space-between"}
      flexDirection={"column"}
      sx={{
        border: borderCondition,
        boxShadow: "0px 4px 4px 0px #EEEEEE",
      }}
    >
      {!sessionClient ||
      isLoading ||
      isFetching ||
      isRefetching ||
      (!thoughtPieces && !isError && !isLoadingError && !isRefetchError) ? (
        [1, 2, 3, 4]?.map((item) => (
          <Box
            key={item}
            sx={{
              padding: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                borderRadius: "8px",
              }}
            >
              <Box
                style={{
                  paddingTop: "0px",
                  paddingLeft: "0px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  width="99px"
                  height={102.85}
                  sx={{
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                  }}
                />
              </Box>
              <Box
                style={{
                  paddingTop: "0px",
                  paddingLeft: "0px",
                  width: "80%",
                  marginLeft: "8px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  style={{
                    height: "100%",
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    marginLeft: "8px",
                    marginRight: "9px",
                  }}
                >
                  <Stack direction="row" width={"100%"}>
                    <Box width={"100%"}>
                      <Skeleton animation="wave" variant="text" height={24} />
                    </Box>
                    <TextSeparator
                      separator="|"
                      distanceX="7px"
                      fontSize="12px"
                      paddingTop="2.5px"
                    />
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                      }}
                    >
                      <Skeleton
                        animation="wave"
                        variant="text"
                        height={24}
                        width={"100%"}
                      />
                    </Box>
                  </Stack>
                  <Skeleton animation="wave" variant="text" height={26} />
                  <Skeleton animation="wave" variant="text" height={26} />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    height={20}
                    width={"30%"}
                    style={{
                      marginTop: "6px",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ))
      ) : !thoughtPieces ||
        isError ||
        isLoadingError ||
        isRefetchError ||
        !Array.isArray(thoughtPieces) ||
        thoughtPieces.length === 0 ? (
        <>
          {[1, 2, 3, 4]?.map((item) => (
            <Box
              key={item}
              sx={{
                padding: "8px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  borderRadius: "8px",
                  filter: "blur(5px)",
                }}
              >
                <Box
                  style={{
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Skeleton
                    animation="wave"
                    variant="rectangular"
                    width="99px"
                    height={102.85}
                    sx={{
                      borderTopLeftRadius: "4px",
                      borderBottomLeftRadius: "4px",
                      borderTopRightRadius: "4px",
                      borderBottomRightRadius: "4px",
                    }}
                  />
                </Box>
                <Box
                  style={{
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    width: "80%",
                    marginLeft: "8px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    style={{
                      height: "100%",
                      paddingTop: "0px",
                      paddingLeft: "0px",
                      marginLeft: "8px",
                      marginRight: "9px",
                    }}
                  >
                    <Stack direction="row" width={"100%"}>
                      <Box width={"100%"}>
                        <Skeleton animation="wave" variant="text" height={24} />
                      </Box>
                      <TextSeparator
                        separator="|"
                        distanceX="7px"
                        fontSize="12px"
                        paddingTop="2.5px"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                        }}
                      >
                        <Skeleton
                          animation="wave"
                          variant="text"
                          height={24}
                          width={"100%"}
                        />
                      </Box>
                    </Stack>
                    <Skeleton animation="wave" variant="text" height={26} />
                    <Skeleton animation="wave" variant="text" height={26} />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={20}
                      width={"30%"}
                      style={{
                        marginTop: "6px",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}{" "}
          <ErrorMessage
            message={"This content is not available right now"}
            messageStyle={{fontSize: "14px", color: "#595958"}}
            refetch={refetch}
          ></ErrorMessage>
        </>
      ) : (
        thoughtPieces.map((item: ThoughtPiecesModel) => (
          <Box
            key={item.group}
            sx={{
              padding: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                borderRadius: "8px",
                textDecoration: "none",
                "&:hover": {
                  backgroundColor: "#EEEEEE",
                  cursor: "pointer",
                },
                "&:hover > div:first-child": {
                  boxShadow:
                    "0 0 8px 0 rgba(0, 0, 0, 0.12), 0 4px 8px 0 rgba(0, 0, 0, 0.14)",
                },
                '&:hover [data-parent-hover="true"] p': {
                  textDecoration: "underline 1px",
                  cursor: "pointer",
                },
              }}
              component={Link}
              to={item.itemUrl}
              onClick={() => setId(item.group)}
            >
              <Box
                style={{
                  paddingTop: "0px",
                  paddingLeft: "0px",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "4px",
                }}
              >
                <ImageComponent
                  src={cmsImageUrl + item.imageMedium}
                  alt={item.title}
                  width="99px"
                  height="102.85px"
                  style={{
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                  }}
                ></ImageComponent>
              </Box>
              <Box
                style={{
                  paddingTop: "0px",
                  paddingLeft: "0px",
                  marginLeft: "8px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <DiscoverSidePanelItem
                  title={item.title}
                  articleTag={
                    item.servicesDisplayTags[0]
                      ? item.servicesDisplayTags[0]
                      : item.mainTagTitle
                      ? item.mainTagTitle
                      : "Our Thinking"
                  }
                  date={moment(item.date)
                    .format("D MMM, YYYY")
                    .replace(".", "")}
                  author={item.authors}
                ></DiscoverSidePanelItem>
              </Box>
            </Box>
          </Box>
        ))
      )}
    </Box>
  )
}

export default DiscoverSidePanel
